/**
 * DeductionRiderToHeadPage Component
 * 라이더 → 본사 일차감 페이지
 *
 * 본사가 라이더에게 부과하는 일차감 관리
 * - 조회: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='C'
 * - 등록: DEDUCT_TARGET='R', DEDUCT_REG_TYPE='C'
 *
 * 테이블 컬럼: No, 대상(협력사), 세부대상(라이더), 공제타입, 공제명, 공제금액, 기간
 */
import React, { useEffect, useState, useCallback } from "react";
import { deductionApi, DeductionDto, riderApi, RiderInfoDto } from "@/shared/api/deductionApi";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { usePagination } from "@/shared/hooks/usePagination";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { formatCurrency } from "@/shared/utils/formatter";
import { getItem } from "@/shared/utils/storage";
import DateInput from "@/shared/components/forms/DateInput";
import { supplierApi } from "@/features/partner/supplierStatus/api/SupplierApi";
import { Supplier } from "@/features/partner/supplierStatus/types/supplier.types";
import { commonCodeApi } from "@/shared/api/commonCodeApi";
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

/** 새 일차감 등록 폼 데이터 타입 */
interface NewDeductionForm {
    suppId: string;
    riderId: string;
    deductionTypeCode: string;
    deductionName: string;
    deductionAmount: string;
    startDate: string;
    endDate: string;
}

const DeductionRiderToHeadPage: React.FC = () => {
    // Toast
    const { showToast } = useToast();

    // 로딩 상태
    const [loading, setLoading] = useState(false);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [ridersLoading, setRidersLoading] = useState(false);
    const [deductionTypesLoading, setDeductionTypesLoading] = useState(false);

    // 데이터
    const [deductions, setDeductions] = useState<DeductionDto[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [riders, setRiders] = useState<RiderInfoDto[]>([]);
    const [deductionTypes, setDeductionTypes] = useState<Array<{ code: string; name: string }>>([]);

    // 필터
    const [filterSuppId, setFilterSuppId] = useState<string>('');

    // 새 일차감 등록 폼
    const [newDeduction, setNewDeduction] = useState<NewDeductionForm>({
        suppId: '',
        riderId: '',
        deductionTypeCode: '',
        deductionName: '',
        deductionAmount: '',
        startDate: '',
        endDate: ''
    });

    // 등록 폼용 라이더 목록 (선택한 협력사 기준)
    const [formRiders, setFormRiders] = useState<RiderInfoDto[]>([]);
    const [formRidersLoading, setFormRidersLoading] = useState(false);

    // 페이지네이션
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE });

    /**
     * 공제 타입 목록 조회 (vr_comm_code에서 FEE_DEDUCT 그룹)
     */
    const fetchDeductionTypes = useCallback(async () => {
        try {
            setDeductionTypesLoading(true);
            const response = await commonCodeApi.getCommonCodeList('FEE_DEDUCT');
            const types = response.codes
                .filter(code => code.useYn === 'Y')
                .map(code => ({
                    code: code.comCode,
                    name: code.comCdName
                }));
            setDeductionTypes(types);
        } catch {
            showToast('공제 타입 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setDeductionTypesLoading(false);
        }
    }, [showToast]);

    /**
     * 협력사 목록 조회
     */
    const fetchSuppliers = useCallback(async () => {
        try {
            setSuppliersLoading(true);
            const response = await supplierApi.getSupplierList();
            const supplierList = response.suppliers.map(s => ({
                id: s.suppId,
                name: s.suppName || s.suppId,
                status: ''
            }));
            setSuppliers(supplierList);
        } catch {
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSuppliersLoading(false);
        }
    }, [showToast]);

    /**
     * 라이더 목록 조회 (협력사별)
     */
    const fetchRiders = useCallback(async (suppId: string) => {
        if (!suppId) {
            setRiders([]);
            return;
        }
        try {
            setRidersLoading(true);
            const response = await riderApi.getRiderList(suppId);
            setRiders(response.riders || []);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setRiders([]);
        } finally {
            setRidersLoading(false);
        }
    }, [showToast]);

    /**
     * 등록 폼용 라이더 목록 조회
     */
    const fetchFormRiders = useCallback(async (suppId: string) => {
        if (!suppId) {
            setFormRiders([]);
            return;
        }
        try {
            setFormRidersLoading(true);
            const response = await riderApi.getRiderList(suppId);
            setFormRiders(response.riders || []);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setFormRiders([]);
        } finally {
            setFormRidersLoading(false);
        }
    }, [showToast]);

    /**
     * 일차감 목록 조회
     * 조건: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='C'
     */
    const fetchDeductions = useCallback(async (suppId?: string) => {
        try {
            setLoading(true);

            const response = await deductionApi.getDeductionList({
                suppId: suppId || undefined,
                deductTarget: 'R'
            });

            // 클라이언트 측 필터링: deductTarget='R' AND deductRegType='C'
            const filteredDeductions = (response.deducts || []).filter(d =>
                d.deductTarget === 'R' && d.deductRegType === 'C'
            );

            // seqNo 기준 내림차순 정렬 (최신순)
            filteredDeductions.sort((a, b) => b.seqNo - a.seqNo);

            setDeductions(filteredDeductions);
        } catch {
            showToast('일차감 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 초기 데이터 로드
     */
    useEffect(() => {
        fetchDeductionTypes();
        fetchSuppliers();
        fetchDeductions();
    }, [fetchDeductionTypes, fetchSuppliers, fetchDeductions]);

    /**
     * 조회 버튼 클릭
     */
    const handleSearch = () => {
        fetchDeductions(filterSuppId);
        resetPagination();
    };

    /**
     * 등록 폼 - 협력사 선택 시 라이더 목록 조회
     */
    const handleFormSuppIdChange = (suppId: string) => {
        setNewDeduction({
            ...newDeduction,
            suppId: suppId,
            riderId: '' // 협력사 변경 시 라이더 초기화
        });
        fetchFormRiders(suppId);
    };

    /**
     * 새 일차감 등록
     */
    const handleAddDeduction = async () => {
        // 유효성 검사
        if (!newDeduction.suppId) {
            showToast('협력사를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.riderId) {
            showToast('라이더를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionTypeCode) {
            showToast('공제타입을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionName) {
            showToast('공제명을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionAmount) {
            showToast('공제금액을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.startDate) {
            showToast('시작일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.endDate) {
            showToast('종료일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        try {
            const userId = getItem<string>('USER_ID') || '';
            const response = await deductionApi.registerDeduction({
                deductTarget: 'R',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1', // 정액
                deductName: newDeduction.deductionName,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insUser: userId
            });

            showToast('일차감이 등록되었습니다.', { type: ToastType.SUCCESS });

            // 낙관적 업데이트: 새로 등록한 데이터를 즉시 목록 맨 앞에 추가
            const newDeductionData: DeductionDto = {
                seqNo: response.seqNo || Date.now(),
                deductTarget: 'R',
                deductRegType: 'C',
                suppId: newDeduction.suppId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1',
                deductName: newDeduction.deductionName,
                deductCnt: null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductRate: null,
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insDate: new Date().toISOString(),
                insUser: userId,
                updDate: null,
                updUser: null
            };

            // 목록 맨 앞에 새 데이터 추가
            setDeductions(prev => [newDeductionData, ...prev]);

            // 폼 초기화
            setNewDeduction({
                suppId: '',
                riderId: '',
                deductionTypeCode: '',
                deductionName: '',
                deductionAmount: '',
                startDate: '',
                endDate: ''
            });
            setFormRiders([]);

            // 페이지네이션 초기화 (첫 페이지로)
            resetPagination();
        } catch {
            showToast('일차감 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    /**
     * 협력사 옵션 변환
     */
    const supplierOptions: SelectOption[] = suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name
    }));

    /**
     * 등록 폼용 라이더 옵션 변환
     * 형식: 라이더이름(라이더아이디)
     */
    const formRiderOptions: SelectOption[] = formRiders.map((rider) => ({
        value: rider.riderId,
        label: rider.riderName ? `${rider.riderId} (${rider.riderName})` : rider.riderId
    }));

    /**
     * 협력사명 조회
     */
    const getSupplierName = (suppId: string): string => {
        const supplier = suppliers.find(s => s.id === suppId);
        return supplier ? supplier.name : suppId;
    };

    /**
     * 라이더명 조회 (deduction 데이터에서)
     */
    const getRiderName = (deduction: DeductionDto): string => {
        // 데이터에 라이더 이름이 있으면 사용, 없으면 riderId 표시
        return deduction.riderId || '-';
    };

    /**
     * 공제타입명 조회
     */
    const getDeductionTypeName = (code: string): string => {
        const type = deductionTypes.find(t => t.code === code);
        return type ? type.name : code;
    };

    /**
     * 기간 포맷 (시작일 ~ 종료일)
     */
    const formatPeriod = (startDay: string, endDay: string): string => {
        if (!startDay && !endDay) return '-';
        if (!endDay) return `${startDay} ~`;
        if (!startDay) return `~ ${endDay}`;
        return `${startDay} ~ ${endDay}`;
    };

    // 로딩 중
    if (loading && deductions.length === 0) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">라이더 → 본사 일차감</h1>
                </div>
                <Loading message="일차감 내역을 불러오는 중..." />
            </div>
        );
    }

    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 → 본사 일차감</h1>
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form rider-to-head-search-form">
                        {/* 협력사 필터 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filterSuppId}
                                    onChange={setFilterSuppId}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 선택'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>

                        {/* 조회 버튼 */}
                        <div className="form-group button-group">
                            <button
                                className="btn btn-primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                <i className="fas fa-search" />
                                조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="supplier-selection-table" style={{ minWidth: '1000px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '60px' }}>NO</th>
                            <th style={{ width: '180px' }}>대상</th>
                            <th style={{ width: '180px' }}>세부대상</th>
                            <th style={{ width: '120px' }}>공제타입</th>
                            <th style={{ width: '180px' }}>공제명</th>
                            <th style={{ width: '120px' }}>공제금액</th>
                            <th style={{ width: '180px' }}>기간</th>
                            <th style={{ width: '80px' }}>등록</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* 기존 데이터 행 */}
                        {currentItems.length === 0 && !loading ? (
                            <tr>
                                <td colSpan={8} className="text-center text-gray-600">
                                    조회된 일차감 내역이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((deduction, index) => (
                                <tr key={deduction.seqNo}>
                                    <td className="text-center">
                                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                    </td>
                                    <td>{getSupplierName(deduction.suppId)}</td>
                                    <td>{getRiderName(deduction)}</td>
                                    <td className="text-center">{getDeductionTypeName(deduction.deductType)}</td>
                                    <td>{deduction.deductName}</td>
                                    <td className="text-right">{formatCurrency(String(deduction.deductAmt || 0))}원</td>
                                    <td className="text-center">{formatPeriod(deduction.startDay, deduction.endDay)}</td>
                                    <td></td>
                                </tr>
                            ))
                        )}

                        {/* 새 일차감 등록 행 */}
                        <tr className="add-row" style={{ backgroundColor: '#f8f9fa' }}>
                            <td className="text-center text-gray-600">+</td>
                            {/* 대상: 협력사 선택 */}
                            <td>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={newDeduction.suppId}
                                    onChange={handleFormSuppIdChange}
                                    placeholder="협력사 선택"
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="선택"
                                />
                            </td>
                            {/* 세부대상: 라이더 선택 */}
                            <td>
                                <SearchableSelect
                                    options={formRiderOptions}
                                    value={newDeduction.riderId}
                                    onChange={(value) => setNewDeduction({ ...newDeduction, riderId: value })}
                                    placeholder={formRidersLoading ? '로딩 중...' : '선택'}
                                    disabled={!newDeduction.suppId || formRidersLoading}
                                    showAllOption={true}
                                    allOptionLabel="선택"
                                />
                            </td>
                            {/* 공제타입 */}
                            <td>
                                <select
                                    className="form-select"
                                    value={newDeduction.deductionTypeCode}
                                    onChange={(e) => {
                                        const code = e.target.value;
                                        const type = deductionTypes.find(t => t.code === code);
                                        setNewDeduction({
                                            ...newDeduction,
                                            deductionTypeCode: code,
                                            deductionName: type ? type.name : ''
                                        });
                                    }}
                                    disabled={deductionTypesLoading}
                                    style={{ width: '100%' }}
                                >
                                    <option value="">{deductionTypesLoading ? '로딩 중...' : '선택'}</option>
                                    {deductionTypes.map(type => (
                                        <option key={type.code} value={type.code}>{type.name}</option>
                                    ))}
                                </select>
                            </td>
                            {/* 공제명 */}
                            <td>
                                <input
                                    type="text"
                                    className="filter-input"
                                    value={newDeduction.deductionName}
                                    onChange={(e) => setNewDeduction({ ...newDeduction, deductionName: e.target.value })}
                                    placeholder="공제명"
                                    style={{ width: '100%' }}
                                />
                            </td>
                            {/* 공제금액 */}
                            <td>
                                <input
                                    type="text"
                                    className="filter-input"
                                    value={formatCurrency(newDeduction.deductionAmount)}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setNewDeduction({ ...newDeduction, deductionAmount: value });
                                    }}
                                    placeholder="금액"
                                    style={{ textAlign: 'right' }}
                                />
                            </td>
                            {/* 기간 */}
                            <td>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <DateInput
                                        id="newStartDate"
                                        value={newDeduction.startDate}
                                        onChange={(value) => setNewDeduction({ ...newDeduction, startDate: value })}
                                    />
                                    <span>~</span>
                                    <DateInput
                                        id="newEndDate"
                                        value={newDeduction.endDate}
                                        onChange={(value) => setNewDeduction({ ...newDeduction, endDate: value })}
                                    />
                                </div>
                            </td>
                            {/* 등록 버튼 */}
                            <td className="text-center">
                                <button
                                    className="btn btn-primary btn-register"
                                    onClick={handleAddDeduction}
                                >
                                    <i className="fas fa-plus-circle" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default DeductionRiderToHeadPage;
